/**
 * Guardrails for the Mongo MCP connector: which collections Claude is even
 * allowed to query (allowlist, fail-closed — a collection with no row here
 * is not queryable), and which field names get stripped from the documents
 * it does see.
 *
 * Fields can be hidden in two places:
 *   - Database-wide ("Campos ocultos en todas las colecciones"): generic
 *     personal data such as `name`, `address` or `nif` that shows up in many
 *     collections. The gateway strips them from every result on this
 *     database, whatever collection was queried — including collections
 *     allowed later.
 *   - Per collection: the third column (McpFieldPicker), shown for the
 *     allowed collection currently checked in the "Permitidas" list.
 *
 * builder-backend is the single authority for this config (Postgres-backed);
 * this panel writes to it directly. mcp-gateway enforces it by polling it —
 * there is no push, so a save takes effect within its poll interval (~60s).
 * The pure logic lives in utils/mcpGuardrails.ts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Select, Space, Spin, Tag, Transfer, Typography } from 'antd';
import type { TransferProps } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import {
  fetchMcpCollections,
  fetchMcpPolicy,
  fetchMcpSampleFields,
  saveMcpPolicy,
} from '../../services/api.service';
import {
  buildSavePayload,
  fieldsForCollection,
  keepHiddenForAllowed,
  newlySelected,
  normalizeFieldNames,
  stateFromPolicy,
} from '../../utils/mcpGuardrails';
import McpFieldPicker from './McpFieldPicker';

const { Text, Paragraph } = Typography;

const LIST_HEIGHT = 480;

interface Props {
  serverId: string;
  database: string;
}

interface TransferRecord {
  key: string;
  title: string;
}

const McpGuardrailsPanel: React.FC<Props> = ({ serverId, database }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const [allCollections, setAllCollections] = useState<string[]>([]);
  const [allowedKeys, setAllowedKeys] = useState<string[]>([]);
  const [globalHiddenFields, setGlobalHiddenFields] = useState<string[]>([]);
  const [hiddenFieldsByCollection, setHiddenFieldsByCollection] = useState<Record<string, string[]>>({});
  const [fieldOptionsByCollection, setFieldOptionsByCollection] = useState<Record<string, string[]>>({});
  const [loadingFieldsFor, setLoadingFieldsFor] = useState<string | null>(null);

  // Allowed collection whose fields are shown in the third column.
  const [activeCollection, setActiveCollection] = useState<string | null>(null);
  const targetSelectionRef = useRef<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [collections, policy] = await Promise.all([fetchMcpCollections(serverId), fetchMcpPolicy(serverId)]);
      const state = stateFromPolicy(policy, database);
      setAllCollections(collections);
      setAllowedKeys(state.allowed);
      setHiddenFieldsByCollection(state.hiddenByCollection);
      setGlobalHiddenFields(state.globalHidden);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la política de Mongo');
    } finally {
      setLoading(false);
    }
  }, [serverId, database]);

  useEffect(() => {
    void load();
  }, [load]);

  const dataSource: TransferRecord[] = useMemo(
    () => allCollections.map((name) => ({ key: name, title: name })),
    [allCollections]
  );
  const globalSet = useMemo(() => new Set(globalHiddenFields), [globalHiddenFields]);

  const loadFieldOptions = useCallback(
    async (collection: string): Promise<void> => {
      if (fieldOptionsByCollection[collection]) return;
      setLoadingFieldsFor(collection);
      try {
        const fields = await fetchMcpSampleFields(serverId, collection);
        setFieldOptionsByCollection((prev) => ({ ...prev, [collection]: fields }));
      } catch {
        // Sampling can fail (empty collection, transient gateway issue); the
        // admin can still hide fields by hand at the bottom of the column.
        setFieldOptionsByCollection((prev) => ({ ...prev, [collection]: [] }));
      } finally {
        setLoadingFieldsFor(null);
      }
    },
    [fieldOptionsByCollection, serverId]
  );

  const handleTransferChange: TransferProps<TransferRecord>['onChange'] = (nextTargetKeys) => {
    const next = nextTargetKeys.map(String);
    setAllowedKeys(next);
    // Hidden fields of a collection moved back to "no permitida" mean nothing.
    setHiddenFieldsByCollection((prev) => keepHiddenForAllowed(prev, next));
    if (activeCollection && !next.includes(activeCollection)) setActiveCollection(null);
  };

  // Checking a collection in "Permitidas" makes it the active one in the
  // fields column (the most recently checked one wins).
  const handleSelectChange: TransferProps<TransferRecord>['onSelectChange'] = (_source, target) => {
    const keys = target.map(String);
    const added = newlySelected(targetSelectionRef.current, keys);
    targetSelectionRef.current = keys;
    if (added) {
      setActiveCollection(added);
      void loadFieldOptions(added);
    }
  };

  const hiddenForActive = activeCollection ? hiddenFieldsByCollection[activeCollection] ?? [] : [];
  const activeFields = activeCollection
    ? fieldsForCollection(fieldOptionsByCollection[activeCollection] ?? [], hiddenForActive)
    : [];

  // Suggestions for the database-wide select: every field sampled so far.
  const globalOptions = useMemo(
    () =>
      fieldsForCollection(Object.values(fieldOptionsByCollection).flat(), globalHiddenFields).map((f) => ({
        value: f,
        label: f,
      })),
    [fieldOptionsByCollection, globalHiddenFields]
  );

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const payload = buildSavePayload({
        allowed: allowedKeys,
        hiddenByCollection: hiddenFieldsByCollection,
        globalHidden: globalHiddenFields,
      });
      await saveMcpPolicy(serverId, database, payload.policies, payload.globalHiddenFields);
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la política de Mongo');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card size="small" title="Guardrails: colecciones y campos">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" title="Guardrails: colecciones y campos" style={{ marginBottom: 16 }}>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Solo las colecciones de la derecha son consultables por Claude — el resto
        se bloquea antes de llegar a Mongo. Los campos ocultos se aplican a cualquier
        profundidad (p. ej. <Text code>contact.email</Text> se oculta con solo
        ocultar <Text code>email</Text>).
      </Paragraph>

      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}

      <div style={{ marginBottom: 20 }}>
        <Text strong>Campos ocultos en todas las colecciones</Text>
        <Paragraph type="secondary" style={{ margin: '4px 0 8px' }}>
          Datos genéricos que pueden aparecer en cualquier colección (nombre, dirección, NIF...).
          Nunca salen hacia Claude, sea cual sea la colección consultada, incluidas las que se
          permitan más adelante.
        </Paragraph>
        <Select
          mode="tags"
          aria-label="Campos ocultos en todas las colecciones"
          style={{ width: '100%' }}
          placeholder="name, address, nif, ..."
          value={globalHiddenFields}
          options={globalOptions}
          tokenSeparators={[',', ' ']}
          onChange={(values: string[]) => setGlobalHiddenFields(normalizeFieldNames(values))}
        />
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '2 1 640px', minWidth: 0 }}>
        <Transfer<TransferRecord>
          dataSource={dataSource}
          titles={['No permitidas', 'Permitidas']}
          targetKeys={allowedKeys}
          onChange={handleTransferChange}
          onSelectChange={handleSelectChange}
          showSearch
          filterOption={(input, item) => item.title.toLowerCase().includes(input.toLowerCase())}
          render={(item) => {
            const hiddenCount = (hiddenFieldsByCollection[item.key] ?? []).filter((f) => !globalSet.has(f)).length;
            return (
              <span style={{ fontWeight: item.key === activeCollection ? 600 : undefined }}>
                {item.title}
                {hiddenCount > 0 && (
                  <Tag color="orange" style={{ marginLeft: 6 }}>
                    {hiddenCount} oculto{hiddenCount === 1 ? '' : 's'}
                  </Tag>
                )}
              </span>
            );
          }}
          listStyle={{ flex: 1, minWidth: 0, width: 'auto', height: LIST_HEIGHT }}
          style={{ width: '100%' }}
        />
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <McpFieldPicker
          key={activeCollection ?? ''}
          collection={activeCollection}
          fields={activeFields}
          hidden={hiddenForActive}
          globalHidden={globalSet}
          loading={activeCollection !== null && loadingFieldsFor === activeCollection}
          height={LIST_HEIGHT}
          onChange={(hidden) => {
            if (activeCollection) setHiddenFieldsByCollection((prev) => ({ ...prev, [activeCollection]: hidden }));
          }}
        />
        </div>
      </div>

      <Space style={{ marginTop: 20 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void handleSave()}>
          Guardar
        </Button>
        {savedAt && (
          <Text type="secondary">
            Guardado a las {savedAt.toLocaleTimeString('es-ES')} — el gateway lo recoge en su próximo sondeo (~60s)
          </Text>
        )}
      </Space>
    </Card>
  );
};

export default McpGuardrailsPanel;
